package com.downdogapp.client

import com.downdogapp.*
import com.downdogapp.client.api.*
import com.downdogapp.client.controllers.playback.PlaybackUtil
import com.downdogapp.client.controllers.start.OfflinePracticesPage
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.Key.OfflineDownloadsInProgress
import com.downdogapp.client.singleton.Key.OfflinePractices
import com.downdogapp.client.singleton.Platform.ANDROID
import kotlinx.serialization.Serializable

object OfflinePractices : LogEmitter {

  private val OFFLINE_DIR = "offlinePractices"

  // Delay between progress updates
  private val PROGRESS_REFRESH_INTERVAL = seconds(1)

  var downloadsInProgress: List<DownloadInProgress> =
    UserPrefs[OfflineDownloadsInProgress]?.let { JSON.decodeFromString(it) }
    ?: listOf()
    private set(value) {
      field = value
      UserPrefs[OfflineDownloadsInProgress] = JSON.encodeToString(value)
    }

  var offlinePractices: List<OfflinePractice> =
    UserPrefs[OfflinePractices]?.let { JSON.decodeFromString(it) }
    ?: listOf()
    private set(value) {
      field = value
      UserPrefs[OfflinePractices] = JSON.encodeToString(value)
    }

  init {
    // create dir for offline practices
    try {
      FileSystem.createDirectory(OFFLINE_DIR)
    } catch (e: Exception) {
      // do nothing if dir already exists
    }

    // check for and remove orphaned .mp4 files
    try {
      for (file in FileSystem.listDirectoryContents(OFFLINE_DIR)) {
        val sequenceId = file.substringAfterLast("/").substringBefore(".mp4")
        if (offlinePractices.none { it.sequence.sequenceId == sequenceId }) {
          FileSystem.removeFile("$OFFLINE_DIR/$sequenceId.mp4")
          logEvent("deleted_orphaned_practice")
        }
      }
    } catch (e: Exception) {
      logEvent("failed_orphaned_mp4_check", mapOf("message" to e.message))
    }

    // resume and complete in progress tasks
    for (download in downloadsInProgress) {
      startNewDownload(download)
      updateStitchAndDownloadProgress(download.url)
    }
  }

  fun onLogout() {
    // cancel downloads in progress
    downloadsInProgress.forEach { cancelDownload(it) }

    // remove offline practices
    offlinePractices.forEach { deleteOfflinePractice(it) }
  }

  fun isDownloading(sequenceId: String) =
    downloadsInProgress.any { it.sequence.sequenceId == sequenceId }

  fun isDownloaded(sequenceId: String) =
    offlinePractices.any { it.sequence.sequenceId == sequenceId }

  fun validateCanDownloadPractice(action: () -> Unit) {
    if (downloadsInProgress.size < MANIFEST.maxConcurrentPracticeDownloads) {
      action()
    } else {
      App.alert(message = Strings.TOO_MANY_DOWNLOADS)
    }
  }

  // Download an existing sequence offline
  fun downloadOfflinePractice(sequenceId: String,
                              onPracticeFetched: () -> Unit) {
    logEvent("download_start")
    downloadPracticeInternal(
      sequenceId = sequenceId,
      sequenceToken = null,
      onPracticeFetched = onPracticeFetched
    )
  }

  // Create a new sequence and immediately generate it offline
  fun createOfflinePractice(sequenceToken: String,
                            onPracticeFetched: () -> Unit) {
    logEvent("create_start")
    downloadPracticeInternal(
      sequenceId = null,
      sequenceToken = sequenceToken,
      onPracticeFetched = onPracticeFetched
    )
  }

  fun deleteOfflinePractice(practice: OfflinePractice) {
    logEvent("practice_removed")
    try {
      FileSystem.removeFile(practice.filePath)
    } catch (e: Exception) {
      logError("failed_delete_offline_file", mapOf("message" to e.message))
    }
    removeOfflinePractice(practice)
  }

  fun cancelDownload(download: DownloadInProgress) {
    // handle race condition - if download already finished, do nothing
    if (!isDownloading(download.sequence.sequenceId)) {
      return
    }

    if (download.stitchProgress < 1) {
      Network.post(
        CancelStitchRequest(url = download.url)
      )
    }

    FileSystem.cancelDownload(download.downloadId)
    removeDownloadInProgressByUrl(download.url)
  }

  private fun downloadPracticeInternal(sequenceId: String?,
                                       sequenceToken: String?,
                                       onPracticeFetched: () -> Unit) {
    Network.post(
      PracticeRequest(
        sequenceId = sequenceId,
        sequenceToken = sequenceToken,
        playlistSettings = SequenceSettings.playlistSettings,
        forOffline = true
      )
    ) { practiceJson ->
      if (practiceJson == null) {
        onPracticeFetched()
        handleError(Error("Failed to fetch practice"))
      } else {
        val practiceResponse = PracticeResponse.fromJson(practiceJson)

        Network.post(
          PlaybackUtil.offlineUrlRequest(
            sequenceId = practiceResponse.sequence.sequenceId,
            playlistId = practiceResponse.playlist?.playlistId
          )
        ) { offlineJson ->
          if (offlineJson == null) {
            onPracticeFetched()
            handleError(Error("Failed to fetch offline practice"))
          } else {
            val offlineResponse = OfflineUrlResponse.fromJson(offlineJson)

            // Kick off the download itself
            val downloadId = initiateDownload(offlineResponse.url, practiceResponse.sequence.sequenceId)

            val download = DownloadInProgress(
              sequence = practiceResponse.sequence,
              playlist = practiceResponse.playlist,
              stitchProgress = 0.0,
              downloadProgress = 0.0,
              url = offlineResponse.url,
              downloadId = downloadId,
              primaryText = offlineResponse.primaryText,
              secondaryText = offlineResponse.secondaryText
            )

            logEvent("download_wait_begin")

            // Add this download so we keep track of it
            addDownloadInProgress(download)

            // Loop and update UI for stitch and download progress
            updateStitchAndDownloadProgress(download.url)

            onPracticeFetched()
          }
        }
      }
    }
  }

  private fun initiateDownload(url: String, sequenceId: String) =
    FileSystemHelper.downloadToFile(
      url,
      "$OFFLINE_DIR/$sequenceId.mp4"
    ) { filePath, error ->
      logEvent("download_finished")

      // If this onComplete is running, there must be a download in progress for this url
      val download = downloadsInProgress.find { it.url == url }!!

      if (error != null) {
        logEvent("download_failed")
        removeDownloadInProgressByUrl(url)
        handleError(error)
        return@downloadToFile
      }

      // If the file is too small we did not get a valid mp4
      // We check minimum of 100kb, which is well below the smallest mp4 we will ever have
      // This avoids a situation where we downloaded an error page instead of actual mp4 data
      if (FileSystem.getFileSize(filePath) < 1024 * 100) {
        logEvent("download_file_too_small")
        removeDownloadInProgressByUrl(url)
        handleError(Error("Download failed due to non-mp4 file being saved"))
        return@downloadToFile
      }

      logEvent("download_success")
      removeDownloadInProgressByUrl(url)
      addOfflinePractice(
        OfflinePractice(
          sequence = download.sequence,
          playlist = download.playlist,
          filePath = filePath,
          primaryText = download.primaryText,
          secondaryText = download.secondaryText
        )
      )
    }

  private fun startNewDownload(download: DownloadInProgress) {
    FileSystem.cancelDownload(download.downloadId)
    val newDownloadId = initiateDownload(download.url, download.sequence.sequenceId)
    downloadsInProgress = downloadsInProgress.map {
      if (it.url == download.url)
        it.copy(downloadId = newDownloadId)
      else
        it
    }
  }

  private fun updateStitchAndDownloadProgress(url: String) {
    updateStitchProgressUntilCompleted(url) {
      logEvent("stitch_finished")

      downloadsInProgress.find { it.url == url }?.let { download ->
        FileSystem.getDownloadTask(download.downloadId)?.let { downloadTask ->
          // Android has an unconfigurable 30-second retry delay with an expontential backoff on download failures
          // If the stitch finishes while in this window, the user will see a long delay before the download actually retries and starts
          // If this is Android, the app is foregrounded and download hasn't started, we cancel the old one and start a new one so the download starts immediately
          if (App.platform == ANDROID && !App.isBackgrounded() && downloadTask.getProgressProportion() == 0.0) {
            startNewDownload(download)
          }

          updateDownloadProgressUntilCompleted(download.url)
        }
      }

    }
  }

  private fun updateStitchProgressUntilCompleted(url: String, onSuccess: () -> Unit) {
    // If download stops before it finishes, we want to stop running this loop
    if (downloadsInProgress.none { it.url == url }) {
      return
    }

    if (App.isBackgrounded()) {
      App.runAfter(PROGRESS_REFRESH_INTERVAL) {
        updateStitchProgressUntilCompleted(
          url = url,
          onSuccess = onSuccess
        )
      }
      return
    }

    Network.post(
      StitchProgressRequest(
        url = url,
      )
    ) { progressJson ->
      if (progressJson == null) {
        logEvent("stitch_progress_network_failure")
        App.runAfter(PROGRESS_REFRESH_INTERVAL) {
          updateStitchProgressUntilCompleted(
            url = url,
            onSuccess = onSuccess
          )
        }
        return@post
      }

      val progressResponse = StitchProgressResponse.fromJson(progressJson)

      progressResponse.error?.let { error ->
        App.runAfter(seconds(1)) {
          // If there is still a matching download in progress, we error out
          downloadsInProgress.find { it.url == url }?.let {
            FileSystem.cancelDownload(it.downloadId)
            removeDownloadInProgressByUrl(url)
            logEvent("stitch_progress_failed")
            handleError(error = Error(error), message = error)
          }
        }
        return@post
      }

      val progress = progressResponse.completedProportion

      updateStitchProgress(url, progress)

      if (progress < 1.0) {
        App.runAfter(PROGRESS_REFRESH_INTERVAL) {
          updateStitchProgressUntilCompleted(
            url = url,
            onSuccess = onSuccess
          )
        }
      } else {
        onSuccess()
      }
    }
  }

  private fun updateDownloadProgressUntilCompleted(url: String) {
    // If download stops before it finishes, we want to stop running this loop
    val downloadInProgress =
      downloadsInProgress.find { it.url == url }
      ?: return

    if (App.isBackgrounded()) {
      App.runAfter(PROGRESS_REFRESH_INTERVAL) {
        updateDownloadProgressUntilCompleted(url)
      }
      return
    }

    FileSystem.getDownloadTask(downloadInProgress.downloadId)?.let { downloadTask ->
      val progress = downloadTask.getProgressProportion()

      // negative progress means download was cancelled externally (e.g. via Android download notification)
      if (progress < 0) {
        removeDownloadInProgressByUrl(url)
        return
      }

      // update progress for this element - urls are guaranteed unique
      updateDownloadProgress(url, progress)

      if (progress < 1.0) {
        App.runAfter(PROGRESS_REFRESH_INTERVAL) {
          updateDownloadProgressUntilCompleted(url)
        }
      }
    }
  }

  private fun handleError(error: Error, message: String? = null) {
    logError("failed_to_download_practice", mapOf("message" to error.message))
    App.alert(
      title = Strings.FAILED_PRACTICE_DOWNLOAD,
      message = message ?: Strings.FAILED_PRACTICE_DOWNLOAD_EXPLANATION,
    )
  }

  private fun addDownloadInProgress(downloadInProgress: DownloadInProgress) {
    downloadsInProgress = downloadsInProgress + downloadInProgress
    OfflinePracticesPage.refreshPage()
  }

  private fun removeDownloadInProgressByUrl(url: String?) {
    downloadsInProgress = downloadsInProgress.filterNot {
      it.url == url
    }
    OfflinePracticesPage.refreshPage()
  }

  private fun updateStitchProgress(url: String, stitchProgress: Double) {
    downloadsInProgress = downloadsInProgress.map {
      if (it.url == url)
        it.copy(stitchProgress = stitchProgress)
      else
        it
    }
    OfflinePracticesPage.refreshPage()
  }

  private fun updateDownloadProgress(url: String, downloadProgress: Double) {
    downloadsInProgress = downloadsInProgress.map {
      if (it.url == url)
        it.copy(downloadProgress = downloadProgress)
      else
        it
    }
    OfflinePracticesPage.refreshPage()
  }

  private fun addOfflinePractice(offlinePractice: OfflinePractice) {
    offlinePractices = offlinePractices + offlinePractice
    OfflinePracticesPage.refreshPage()
  }

  private fun removeOfflinePractice(offlinePractice: OfflinePractice) {
    offlinePractices = offlinePractices - offlinePractice
    OfflinePracticesPage.refreshPage()
  }

  @Serializable
  data class DownloadInProgress(val sequence: Sequence,
                                val playlist: Playlist?,
                                val stitchProgress: Double,
                                val downloadProgress: Double,
                                val downloadId: Long,
                                val url: String,
                                val primaryText: String,
                                val secondaryText: String)

  @Serializable
  data class OfflinePractice(val sequence: Sequence,
                             val playlist: Playlist?,
                             val filePath: String,
                             val primaryText: String,
                             val secondaryText: String)
}
